<script setup>
import {computed} from "vue";
import {Table, THead, TBody, TH, TR, TD} from "@/Components/Table";
import CheckIndicator
    from "@/Pages/Education/ReceptionCompany/CompetitionLists/components/CompetitionListV2/CheckIndicator.vue";

const props = defineProps(['cgGuid', 'isPay', 'isTargeted', 'isSVE', 'isSorted', 'position', 'application']);

const colorClass = computed(() => {
    if(props.application.state === 'Зачислен') return 'bg-green-200';
    if(props.application.state === 'Зачислен по другому направлению') return 'bg-blue-100';
    if(props.application.state === 'Рекомендован к зачислению, если бы зачисление проводилось сейчас') return 'bg-green-200';
    if(props.application.state === 'Проходит по этому приоритету') return 'bg-green-200';
    if(props.application.state === 'Проходит по другому приоритету') return 'bg-blue-100';
    if(props.application.state === 'Отозвано') return 'bg-red-200/75';
    if(props.application.state === 'Выбыл из конкурса') return 'bg-red-200';

    return '';
});

const showMainPriority = computed(() => {
    return !!props.application.priority.main && props.application.priority.main !== 0
});

const showUpperPriority = computed(() => {
    return !!props.application.priority.upper && props.application.priority.upper !== 0
});

const showPriorityHoverBlock = computed(() => {
    return showMainPriority.value || showUpperPriority.value;
});
</script>

<template>
    <tr :class="['border relative', colorClass]">
        <TD class="!px-2 !py-1 border !text-sm text-center whitespace-nowrap">
            <template v-if="isSorted">
                {{ props.position }}<br/>
                <span class="text-xs text-gray-500">({{ props.application.position }})</span>
            </template>

            <template v-else>
                {{ props.application.position }}
            </template>
        </TD>
        <TD :colspan="!isTargeted ? 2 : 1" class="!px-2 !py-1 border !text-sm text-center">{{ application.code ?? '-' }}</TD>
        <TD v-if="isTargeted" class="!px-2 !py-1 border !text-sm text-center">
            <template v-if="!!application.targeted?.number">
                <template v-if="!!application.targeted?.organization && !!application.targeted?.count">
                    <div class="relative">
                        <div class="peer">
                            <span class="text-blue-600 hover:text-blue-900 cursor-pointer">{{ application.targeted.number + (application.targeted.is_rvr ? '' : `_${application.targeted.customer_uid}`) }}</span>
                            <br/>
                            <CheckIndicator :value="application.targeted.is_rvr"/>
                        </div>


                        <div class="hidden peer-hover:flex hover:flex absolute bottom-1/2 -left-[100px] bg-white border rounded w-[480px]">
                            <Table>
                                <THead class="border">
                                    <TH class="border">Заказчик</TH>
                                    <TH class="border">Кол-во договоров</TH>
                                </THead>
                                <TBody class="border">
                                    <TR class="border">
                                        <TD class="border !px-2 !py-1 w-full">{{ application.targeted?.organization }}</TD>
                                        <TD class="border !px-w !py-1 whitespace-nowrap">{{ application.targeted?.count }}</TD>
                                    </TR>
                                </TBody>
                            </Table>
                        </div>
                    </div>
                </template>

                <template v-else>
                    {{ application.targeted.number + (application.targeted.is_rvr ? '' : `_${application.targeted.customer_uid}`) }}
                    <br/>
                    <CheckIndicator :value="application.targeted.is_rvr"/>
                </template>
            </template>

            <template v-else>-</template>
        </TD>
        <TD v-if="!isPay || isSVE" :colspan="isSVE ? (isPay ? 1 : 3) : 4" class="!px-2 !py-1 border !text-sm text-center">
            <CheckIndicator :value="application.agreement"/>
        </TD>
        <template v-if="isSVE">
            <TD class="!px-2 !py-1 border !text-sm text-center"><CheckIndicator :value="application.original"/></TD>
        </template>
        <template v-if="isPay">
            <TD :colspan="!isSVE ? 3 : 1" class="!px-2 !py-1 border !text-sm text-center">
                <CheckIndicator :value="application?.payment?.state === 'Утвержден'"/>
            </TD>
            <TD class="!px-2 !py-1 border !text-sm text-center">
                <CheckIndicator :value="application.payment.is_payment"/>
            </TD>
        </template>

        <TD class="!px-2 !py-1 border !text-sm text-center" v-if="isSVE">{{ application.points.certificate_total }}</TD>
        <TD class="!px-2 !py-1 border !text-sm text-center">{{ isSVE ? application.points.certificate_avg : application.points.total }}</TD>
        <TD class="!px-2 !py-1 border !text-sm text-center">
            <span v-if="application.is_bvi">Без ВИ</span>
            <span v-else-if="application.points.total_by_disciplines === 0 && application.points.disciplines.length === 0">0</span>
            <template v-else>
                <span class="peer text-blue-600 hover:text-blue-900 cursor-pointer">{{ application.points.total_by_disciplines }}</span>

                <div class="hidden peer-hover:flex hover:flex absolute bottom-1/2 right-1/4 bg-white border rounded w-1/2">
                    <Table>
                        <THead class="border">
                        <TH class="border">Предмет</TH>
                        <TH class="border">Кол-во баллов</TH>
                        </THead>
                        <TBody class="border">
                            <TR class="border"
                                v-for="(discipline, disciplineIndex) in application.points.disciplines"
                                :key="cgGuid + '-' + application.position + '-discipline-' + disciplineIndex"
                            >
                                <TD class="border !px-2 !py-1 w-full">{{ discipline.name }}</TD>
                                <TD class="border !px-w !py-1 whitespace-nowrap">
                                    {{ discipline.points }}
                                </TD>
                            </TR>
                        </TBody>
                    </Table>
                </div>
            </template>
        </TD>
        <TD class="!px-2 !py-1 border !text-sm text-center">{{ application.points.total_by_achievements_concurs }}</TD>
        <TD class="!px-2 !py-1 border !text-sm text-center" v-if="!isSVE">
            <span v-if="!showPriorityHoverBlock" >{{ application.priority.priority }}</span>

            <template v-else>
                <div class="relative">
                    <span class="peer text-blue-600 hover:text-blue-900 cursor-pointer">{{ application.priority.priority }}</span>

                    <div class="hidden peer-hover:flex hover:flex absolute bottom-1/2 -left-[180px] bg-white border rounded w-[360px]">
                        <Table>
                            <TBody class="border">
                                <TR v-if="showMainPriority">
                                    <TD class="border !px-2 !py-1 w-full">Основной высший приоритет</TD>
                                    <TD class="border !px-2 !py-1 whitespace-nowrap">{{ application?.priority?.main }}</TD>
                                </TR>
                                <TR v-if="showUpperPriority">
                                    <TD class="border !px-2 !py-1 w-full">Высший проходной приоритет</TD>
                                    <TD class="border !px-2 !py-1 whitespace-nowrap">{{ application?.priority?.upper }}</TD>
                                </TR>
                            </TBody>
                        </Table>
                    </div>
                </div>
            </template>
        </TD>
        <TD class="!px-2 !py-1 border !text-sm text-center">
            <span v-if="!(application.is_bvi || !!application.preemptive_right)">-</span>

            <template v-else>
                <ul class="ml-6 list-disc text-left">
                    <li v-if="application.is_bvi">Основание для БВИ</li>
                    <li v-if="!!application.preemptive_right">Преимущественное право</li>
                </ul>
            </template>
        </TD>
        <TD class="!px-2 !py-1 border !text-sm text-center">{{ application.state }}</TD>
    </tr>
</template>

<style scoped>

</style>
